import axios from "axios";
import { colors } from "constants/colors";
import { assetsRoot } from "config";
import React, { FC, JSX, useState } from "react";
import Link from "next/link";

import st from "./footer.module.scss";

const F = `${assetsRoot}/poppi/footer`;

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

// poppi footer columns. Labels mirror drinkpoppi.com; internal hrefs are mapped
// to routes that exist in this app, external ones keep their real destinations.
const COLUMNS: FooterColumn[] = [
  {
    title: "Shop",
    links: [{ label: "shop pop", href: "/collections/drinks" }],
  },
  {
    title: "Learn",
    links: [
      { label: "our story", href: "/pages/our-story" },
      { label: "why poppi?", href: "/pages/whypoppi" },
      { label: "find poppi", href: "/pages/find-poppi" },
    ],
  },
  {
    title: "More",
    links: [
      // per design review: contact + privacy/legal route to the PEP CSX
      // consumer-experience flows instead of the internal replica pages
      { label: "contact", href: "https://contact.pepsico.com/poppi", external: true },
      { label: "careers", href: "https://www.pepsicojobs.com/main/", external: true },
      { label: "college", href: "/pages/poppi-university" },
      { label: "partners", href: "https://app.onbrand.com/brand/poppi/", external: true },
      { label: "privacy", href: "https://policy.pepsi.com/privacy.htm", external: true },
      { label: "legal", href: "https://www.pepsico.com/legal/terms-of-use", external: true },
    ],
  },
];

const SOCIALS: { name: string; href: string; icon: string }[] = [
  { name: "TikTok", href: "https://www.tiktok.com/@drinkpoppi?lang=en", icon: `${F}/tiktok.svg` },
  { name: "Threads", href: "https://www.threads.net/@drinkpoppi", icon: `${F}/threads.svg` },
  { name: "Instagram", href: "https://www.instagram.com/drinkpoppi/", icon: `${F}/instagram.svg` },
];

type SignupState =
  | "idle"
  | "sending"
  | "empty"
  | "invalid"
  | "rateLimited"
  | "error"
  | "done";

// Consumer-facing messages — baseline copy verbatim: empty/invalid measured
// on the 12 Aug live site, the rest taken from the live site's public
// custom.js (per review).
const SIGNUP_MESSAGES: Partial<Record<SignupState, string>> = {
  empty: "Please enter your email address.",
  invalid: "Please enter a valid email address.",
  rateLimited: "Too many attempts. Please wait a moment and try again.",
  error: "There was an error with your submission. Please try again.",
  done: "You're subscribed! Thanks for signing up.",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function renderLink(link: FooterLink): JSX.Element {
  return link.external ? (
    <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className={st.colLink}>
      {link.label}
    </a>
  ) : (
    <Link key={link.label} href={link.href} className={st.colLink}>
      {link.label}
    </Link>
  );
}

export const NavFooter: FC = () => {
  const year = new Date().getFullYear();

  // footer email signup → /api/subscribe → Attentive
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState(""); // decoy field — humans never fill this
  const [signupState, setSignupState] = useState<SignupState>("idle");

  const onSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupState === "sending") return;

    // baseline behavior: distinct empty / invalid messages, validated in JS
    // (the form is noValidate, like live's)
    const trimmed = email.trim();
    if (!trimmed) {
      setSignupState("empty");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setSignupState("invalid");
      return;
    }

    setSignupState("sending");
    try {
      // axios throws on non-2xx, so server failures land in catch
      const { data } = await axios.post<{ ok: boolean }>("/api/subscribe", {
        email: trimmed,
        contact_reason: honeypot,
      });
      if (data.ok) {
        // baseline: show the success message, reset the field, keep the form
        setEmail("");
        setSignupState("done");
      } else {
        setSignupState("error");
      }
    } catch (err) {
      setSignupState(
        axios.isAxiosError(err) && err.response?.status === 429
          ? "rateLimited"
          : "error"
      );
    }
  };

  return (
    <footer className={st.footer}>
      {/* wavy top edge — fill is themed (default pink, PDPs use --color-secondary) */}
      <svg className={st.wave} viewBox="0 0 1440 150" fill="none" preserveAspectRatio="none" aria-hidden="true">
        <path
          d="M0 -0.000106812V151.473H1440L1440 95.0572C1119.5 -45.0001 801.395 39.6621 605.159 65.527C430.843 88.5028 169 115.5 0 -0.000106812Z"
          fill={`var(--footer-bg, var(--color-secondary, ${colors.magenta}))`}
        />
      </svg>

      <div className={st.body}>
        <div className={st.inner}>
          <div className={st.top}>
            <div className={st.signup}>
              <span className={st.smiley} aria-hidden="true" />
              <div className={st.signupContent}>
                <h2 className={st.title}>let&apos;s be friends</h2>
                {/* live's refreshed sign-up unit: the text box sits directly
                    under the heading, with the disclaimer below it */}
                <form
                  className={st.form}
                  onSubmit={onSignupSubmit}
                  noValidate
                  aria-label="email signup"
                >
                  {/* honeypot (baseline trap field) — visually hidden, bots fill it */}
                  <input
                    className={st.honeypot}
                    type="text"
                    name="contact_reason"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                  <div className={st.formMain}>
                  <input
                    className={st.input}
                    type="email"
                    name="email"
                    placeholder="enter your email"
                    aria-label="email address"
                    autoComplete="email"
                    autoCorrect="off"
                    autoCapitalize="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={signupState === "sending"}
                  />
                  <button
                    className={st.submit}
                    type="submit"
                    disabled={signupState === "sending"}
                  >
                    submit
                  </button>
                  </div>
                </form>
                {SIGNUP_MESSAGES[signupState] && (
                  <p className={st.formMessage} role="status">
                    {SIGNUP_MESSAGES[signupState]}
                  </p>
                )}
                <p className={st.consent}>
                  By providing us your email, you agree to receive marketing
                  emails from us, including exclusive emails about new launches,
                  iconic deals, and other exciting announcements. We personalize
                  content based on how you shop and browse.
                </p>
              </div>
            </div>

            <div className={st.socials}>
              {SOCIALS.map((s) => (
                <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" className={st.socialLink} aria-label={s.name}>
                  <span
                    className={st.socialIcon}
                    style={{ maskImage: `url(${s.icon})`, WebkitMaskImage: `url(${s.icon})` }}
                  />
                </a>
              ))}
            </div>
          </div>

          <div className={st.columns}>
            {COLUMNS.map((col) => (
              <div key={col.title} className={st.col}>
                <h3 className={st.colTitle}>{col.title}</h3>
                {col.links.map(renderLink)}
              </div>
            ))}
          </div>

          <div className={st.copyright}>© {year} Poppi</div>
        </div>
      </div>
    </footer>
  );
};
